import { fmtDateTime } from "@/lib/format";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/PageHeader";
import { CompanyLogo } from "@/components/BrandMark";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { cn } from "@/lib/utils";

export const StatusBadge = ({ status, testid }) => (
  <span data-testid={testid} className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold",
    status === "active" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700")}>{status === "active" ? "Active" : "Inactive"}</span>
);

const when = (iso) => fmtDateTime(iso);
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
const EMPTY = { name: "", slug: "", admin_name: "", admin_email: "", admin_password: "" };
const FIELDS = [["name", "Company name", "text"], ["slug", "Slug (URL)", "text"], ["admin_name", "Admin name", "text"],
  ["admin_email", "Admin email", "email"], ["admin_password", "Initial password", "password"]];

const CreateCompanyDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v, ...(k === "name" && (!f.slug || f.slug === slugify(f.name)) ? { slug: slugify(v) } : {}) }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/admin/companies", form);
      (data.email_sent ? toast.success : toast.warning)(data.message);
      setForm(EMPTY);
      onCreated(data.company);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-company-dialog">
        <DialogHeader><DialogTitle>New company</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          {FIELDS.map(([k, label, type]) => (
            <div key={k} className="space-y-1.5">
              <Label htmlFor={`cc-${k}`}>{label}</Label>
              <Input id={`cc-${k}`} type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`create-company-${k.replace("_", "-")}`} />
            </div>
          ))}
          <p className="text-xs text-slate-500">The company starts empty with the default expense mapping rules. A welcome email with the login link is sent to the admin.</p>
          {error && <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="create-company-error">{error}</p>}
          <Button type="submit" disabled={busy} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="create-company-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create company"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function CompaniesPage() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [demo, setDemo] = useState(0);
  const [dups, setDups] = useState(null);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupBusy, setDupBusy] = useState(false);
  const [dupResult, setDupResult] = useState(null);
  const loadDups = () => api.get("/admin/maintenance/duplicate-accounts").then((r) => setDups(r.data)).catch(() => {});
  const runDedupe = async () => {
    setDupBusy(true);
    try {
      const { data } = await api.post("/admin/maintenance/dedupe-accounts");
      setDupResult(data);
      toast.success(`${data.removed} duplicate records removed across ${data.groups} accounts`);
      setDupOpen(false);
      loadDups();
    } catch (e) { toast.error(apiError(e)); } finally { setDupBusy(false); }
  };
  const load = () => api.get("/admin/companies").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); api.get("/admin/maintenance/demo-data").then((r) => setDemo(r.data.demo_records)).catch(() => {}); loadDups(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const removeDemo = async () => {
    if (!window.confirm(`Delete ${demo} leftover demo records? This cannot be undone.`)) return;
    try { const { data } = await api.post("/admin/maintenance/remove-demo-data"); toast.success(`${data.deleted} demo records removed`); setDemo(0); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="companies-page">
      <PageHeader eyebrow="Platform" title="Companies" subtitle="Every tenant on the platform. Data, branding and viewers are isolated per company."
        actions={<div className="flex gap-2">
          {dups?.accounts > 0 && <Button variant="outline" onClick={() => setDupOpen(true)} className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="dedupe-accounts-button">Clean up duplicate accounts ({dups.accounts})</Button>}
          {demo > 0 && <Button variant="outline" onClick={removeDemo} className="border-red-200 text-red-700 hover:bg-red-50" data-testid="remove-demo-data-button">Remove demo data ({demo})</Button>}
          <Button onClick={() => setOpen(true)} className="gap-1.5 bg-teal-600 hover:bg-teal-700" data-testid="new-company-button"><Plus className="h-4 w-4" /> New company</Button>
        </div>} />
      <div className="rise-in overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm" data-testid="companies-table">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>{["Company", "Slug", "Status", "Admins", "Last activity", "Created"].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {(rows || []).map((c) => (
              <tr key={c.id} onClick={() => navigate(`/admin/companies/${c.id}`)} className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50" data-testid={`company-row-${c.slug}`}>
                <td className="px-4 py-3"><div className="flex items-center gap-3"><CompanyLogo company={c} className="h-9 w-14" testid={`company-logo-${c.slug}`} /><span className="font-medium text-slate-900">{c.name}</span></div></td>
                <td className="px-4 py-3 font-mono text-xs text-slate-600">{c.slug}</td>
                <td className="px-4 py-3"><StatusBadge status={c.status} testid={`company-status-${c.slug}`} /></td>
                <td className="px-4 py-3 num" data-testid={`company-admins-${c.slug}`}>{c.admins}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{when(c.last_activity)}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{when(c.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {dupResult && (
        <div className="rounded-lg border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-900" data-testid="dedupe-result">
          Removed {dupResult.removed} duplicate records across {dupResult.groups} accounts (backed up to erp_accounts_dedup_backup).
          {" "}Unique index {dupResult.index_created ? "created" : "NOT created"} · {dupResult.remaining} duplicated accounts remaining.
        </div>
      )}
      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="max-w-2xl" data-testid="dedupe-confirm-dialog">
          <DialogHeader><DialogTitle>Clean up duplicate accounts</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">For each account below, the most recent copy is kept (the one the P&amp;L already uses). The other copies are backed up to <span className="font-mono">erp_accounts_dedup_backup</span> and removed, then the unique index is created.</p>
          <div className="max-h-80 overflow-auto rounded border border-slate-200">
            <table className="w-full text-sm" data-testid="dedupe-groups-table">
              <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-3 py-2">Company</th><th>Account</th><th>Copies</th><th>Categories</th><th>Kept</th></tr></thead>
              <tbody>
                {(dups?.groups || []).map((g) => (
                  <tr key={`${g.company_id}-${g.account}`} className={cn("border-t border-slate-100", g.differing && "bg-amber-50")} data-testid={`dedupe-group-${g.account}`}>
                    <td className="px-3 py-2">{g.company}</td>
                    <td className="font-mono text-xs">{g.account}<div className="font-sans text-[11px] text-slate-500">{g.description}</div></td>
                    <td className="num">{g.copies}</td>
                    <td className="text-xs">{g.categories.join(", ")}{g.differing && <span className="ml-1 rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900" data-testid={`dedupe-differing-${g.account}`}>Categories differ</span>}</td>
                    <td className="text-xs font-medium">{g.kept_category}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDupOpen(false)} data-testid="dedupe-cancel">Cancel</Button>
            <Button onClick={runDedupe} disabled={dupBusy} className="bg-amber-600 hover:bg-amber-700" data-testid="dedupe-confirm">{dupBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Clean up ${dups?.extra_records ?? 0} duplicate records`}</Button>
          </div>
        </DialogContent>
      </Dialog>
      <CreateCompanyDialog open={open} onOpenChange={setOpen} onCreated={() => { setOpen(false); load(); refreshUser(); }} />
    </div>
  );
}
